import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Flex,
  VStack,
  Divider,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { QuestionIcon } from '@chakra-ui/icons';
import AppColors from '../../../constants/AppColors';

// Matches REQUIRED_FIELDS from constants exactly
const REQUIRED_FIELDS_INFO = [
  { field: 'Encounter No', col: 'encounter_no', desc: 'Unique encounter identifier' },
  { field: 'Rendering', col: 'rendering', desc: 'Provider who rendered the service' },
  { field: 'Enc Date', col: 'enc_dt', desc: 'Date of service (MM-DD-YYYY)' },
  { field: 'Srvcs No (CPT)', col: 'srvcs_no', desc: 'Billed procedure code — format CPT*UNITS or CPT' },
  { field: 'Agree', col: 'agree', desc: 'Type Yes or No' },
  { field: 'Disagree', col: 'disagree', desc: 'Auto-set opposite of Agree' },
  { field: 'Audited CPT', col: 'audited_cpt', desc: 'Auto-filled on Agree, enter manually on Disagree' },
  { field: 'Notes', col: 'notes', desc: 'Audit notes — required before submitting' },
  { field: 'Response', col: 'response', desc: 'Select audit issue response from dropdown' },
];

const MISSING_INFO_FIELDS = [
  { field: 'Encounter No', desc: 'Still required when missing info is flagged' },
  { field: 'Rendering', desc: 'Still required when missing info is flagged' },
];

const COLOR_LEGEND = [
  { label: 'Editing', bg: '#fff9e6', border: '#FFB647', desc: 'Unsaved local change' },
  { label: 'Saving', bg: '#e3f2fd', border: '#023B93', desc: 'Sending to server', pulse: true },
  { label: 'Missing Info', bg: '#FBE4D6', border: '#F9480F', desc: 'Row flagged as incomplete' },
];

const EDITING_TIPS = [
  { icon: '🖱️', text: 'Click a cell to select, then type to edit' },
  { icon: '🖱️', text: 'Right-click for context menu — insert rows, export CSV and more' },
  { icon: '💾', text: 'Edited rows will be auto-saved after 10 seconds of inactivity or when you click a different row' },
  { icon: '⚠️', text: 'CPT format is CPT*UNITS or CPT e.g. 99213*1 or 99213. Invalid codes are cleared automatically' },
];

function ColorDot({ bg, border, pulse }) {
  return (
    <Box
      w="28px"
      h="18px"
      borderRadius="3px"
      bg={bg}
      borderLeft={`3px solid ${border}`}
      flexShrink={0}
      sx={pulse ? { animation: 'pulse-saving 1s ease-in-out infinite' } : {}}
    />
  );
}

export default function AuditSheetHelpModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // show on first render after small delay
    const show = setTimeout(() => setShowTooltip(true), 800);
    // auto hide after 4 seconds
    const hide = setTimeout(() => setShowTooltip(false), 4800);
    return () => {
        clearTimeout(show);
        clearTimeout(hide);
    };
    }, []); // empty dep = runs once on mount only

  const onOpen = () => {
    setShowTooltip(false);
    setIsOpen(true);
    };

  const onClose = () => {
    setIsOpen(false);
    setShowDetail(false);
  };

  return (
    <>
      <Tooltip
        label="Click here to learn how to edit the audit sheet"
        isOpen={showTooltip}
        hasArrow
        placement="bottom"
        bg={AppColors.primary}
        color="white"
        fontSize="xs"
        px={3}
        py={2}
        borderRadius="6px"
      >
        <IconButton
          icon={<QuestionIcon />}
          mr={1}
          size="sm"
          variant="ghost"
          color="white"
          aria-label="Help"
          _hover={{ bg: 'whiteAlpha.200' }}
          onClick={onOpen}
        />
      </Tooltip>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={showDetail ? 'lg' : 'sm'}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent borderRadius="10px" overflow="hidden" mx={4}>

          <Box bg={AppColors.secondary} px={5} py={3}>
            <Flex align="center" justify="space-between">
              <Text color="white" fontWeight={700} fontSize="md">
                {showDetail ? 'Editing Guide' : 'Quick Help'}
              </Text>
              <ModalCloseButton color="white" position="relative" top={0} right={0} size="sm" />
            </Flex>
          </Box>

          <ModalBody p={0} bg={AppColors.bgColor}>
            {!showDetail ? (
              <VStack spacing={0} align="stretch">

                <Box px={5} py={4} bg="white">
                  <Text fontSize="xs" fontWeight={700} color={AppColors.secondary} mb={3} textTransform="uppercase" letterSpacing="0.05em">
                    How to Edit
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {EDITING_TIPS.map((t, i) => (
                      <Flex key={i} gap={3} align="flex-start">
                        <Box
                          w="24px" h="24px" borderRadius="5px"
                          bg={AppColors.bgColor}
                          display="flex" alignItems="center" justifyContent="center"
                          flexShrink={0}
                          mt="1px"
                        >
                          <Text fontSize="12px">{t.icon}</Text>
                        </Box>
                        <Text fontSize="xs" color="gray.600" lineHeight={1.6}>{t.text}</Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>

                <Divider />

                <Box px={5} py={4} bg="white">
                  <Text fontSize="xs" fontWeight={700} color={AppColors.secondary} mb={3} textTransform="uppercase" letterSpacing="0.05em">
                    Cell Colors
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {COLOR_LEGEND.map((c, i) => (
                      <Flex key={i} align="center" gap={3}>
                        <ColorDot bg={c.bg} border={c.border} pulse={c.pulse} />
                        <Text fontSize="xs" color="gray.600">
                          <Box as="span" fontWeight={600} color="gray.700">{c.label}</Box>
                          {' — '}{c.desc}
                        </Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>

                <Divider />

                <Box px={5} py={3} bg="#f0fdf4">
                  <Text fontSize="xs" color="green.700">
                    <Box as="span" fontWeight={700}>Submit Audit</Box> activates only when all required fields are filled in every row.
                  </Text>
                </Box>

                <Box px={5} py={4} bg="white">
                  <Button
                    w="full"
                    size="sm"
                    variant="outline"
                    borderColor={AppColors.secondary}
                    color={AppColors.secondary}
                    _hover={{ bg: AppColors.secondary, color: 'white' }}
                    onClick={() => setShowDetail(true)}
                    fontSize="xs"
                  >
                    View Required Fields & Full Guide →
                  </Button>
                </Box>
              </VStack>

            ) : (
              <VStack spacing={0} align="stretch">

                <Box px={5} py={4} bg="white">
                  <Text fontSize="xs" fontWeight={700} color={AppColors.secondary} mb={1} textTransform="uppercase" letterSpacing="0.05em">
                    Required Fields — Normal Rows
                  </Text>
                  <Text fontSize="xs" color="gray.400" mb={3}>All fields below must be filled to enable Submit</Text>
                  <VStack spacing={0} align="stretch" borderRadius="7px" overflow="hidden" border="1px solid" borderColor="gray.100">
                    {REQUIRED_FIELDS_INFO.map((f, i) => (
                      <Flex
                        key={i}
                        px={3} py={2}
                        bg={i % 2 === 0 ? 'white' : AppColors.bgColor}
                        align="center" gap={3}
                        borderBottom={i < REQUIRED_FIELDS_INFO.length - 1 ? '1px solid' : 'none'}
                        borderColor="gray.100"
                      >
                        <Box
                          bg={AppColors.secondary} color="white"
                          fontSize="9px" fontWeight={700}
                          px={2} py={0.5} borderRadius="3px"
                          flexShrink={0}
                          textTransform="uppercase" letterSpacing="0.04em"
                          whiteSpace="nowrap"
                        >
                          {f.field}
                        </Box>
                        <Text fontSize="xs" color="gray.500">{f.desc}</Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>

                <Divider />

                <Box px={5} py={4} bg="white">
                  <Flex align="center" gap={2} mb={1}>
                    <ColorDot bg="#FBE4D6" border="#F9480F" />
                    <Text fontSize="xs" fontWeight={700} color={AppColors.secondary} textTransform="uppercase" letterSpacing="0.05em">
                      Missing Info Rows
                    </Text>
                  </Flex>
                  <Text fontSize="xs" color="gray.400" mb={3}>
                    Right-click → Enable Missing Info, or select M.info to flag a row. Only two fields are required:
                  </Text>
                  <VStack spacing={0} align="stretch" borderRadius="7px" overflow="hidden" border="1px solid" borderColor="gray.100">
                    {MISSING_INFO_FIELDS.map((f, i) => (
                      <Flex
                        key={i}
                        px={3} py={2}
                        bg={i % 2 === 0 ? 'white' : AppColors.bgColor}
                        align="center" gap={3}
                        borderBottom={i < MISSING_INFO_FIELDS.length - 1 ? '1px solid' : 'none'}
                        borderColor="gray.100"
                      >
                        <Box
                          bg={AppColors.orange} color="white"
                          fontSize="9px" fontWeight={700}
                          px={2} py={0.5} borderRadius="3px"
                          flexShrink={0}
                          textTransform="uppercase" letterSpacing="0.04em"
                          whiteSpace="nowrap"
                        >
                          {f.field}
                        </Box>
                        <Text fontSize="xs" color="gray.500">{f.desc}</Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>

                <Divider />

                <Box px={5} py={4} bg="white">
                  <Text fontSize="xs" fontWeight={700} color={AppColors.secondary} mb={3} textTransform="uppercase" letterSpacing="0.05em">
                    Cell Colors
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {COLOR_LEGEND.map((c, i) => (
                      <Flex key={i} align="center" gap={3}>
                        <ColorDot bg={c.bg} border={c.border} pulse={c.pulse} />
                        <Text fontSize="xs" color="gray.600">
                          <Box as="span" fontWeight={600} color="gray.700">{c.label}</Box>
                          {' — '}{c.desc}
                        </Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>

                <Box px={5} py={3} bg="white" borderTop="1px solid" borderColor="gray.100">
                  <Button
                    variant="ghost"
                    size="sm"
                    color="gray.400"
                    fontSize="xs"
                    onClick={() => setShowDetail(false)}
                  >
                    ← Back
                  </Button>
                </Box>

              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}